import { type FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VerticalStepForm from '@/components/UI/VerticalStepForm/VerticalStepForm';
import type { StepConfig } from '@/components/UI/VerticalStepForm/VerticalStepForm.model';
import { agentUrlSlugMap } from '@/components/Agents/AgentUtils';
import {
  CSV_EXTENSION,
  MAX_BUNDLE_FILES,
  createSendBundleUploader,
} from '@/components/Team/AdminConsole/InVivoSendValidator/sendUpload';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { AgentSessionService } from '@/support/AgentSessionService';
import { ResponseType } from '@/support/ApiService';
import { web as webRoute } from '@/support/route';

const CSV_ACCEPT = `${CSV_EXTENSION},text/csv`;

/**
 * Form value shape. `attachment_api_ids` holds the opaque `attachment_api_id`s minted server-side by
 * the multi-file upload field (one per successfully uploaded CSV) — never a key or URL. Domain
 * detection and per-file confirmation no longer happen here: they move to the session page, where the
 * `send_domain_confirm` node opens the confirm modal.
 */
interface SendValidatorSetupValues extends Record<string, unknown> {
  attachment_api_ids: Array<string>;
}

/**
 * AI-hub setup form for the SEND Validator, built on {@link VerticalStepForm}. A single step uploads a
 * bundle of CSVs (one per SEND domain) to a server-minted batch; submitting kicks off the detect
 * execution on a new session and navigates to its stream. The session page then surfaces a
 * `send_domain_confirm` node — the user confirms per-file domains there (not in this form) before
 * validation runs and the `send_result` node appears.
 *
 * Only the opaque batch id crosses the wire; the worker resolves the batch's attachments server-side
 * and rebuilds every S3 key from the team in its security context.
 */
const SendValidatorSetupForm: FC = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // One uploader per form mount: every picked CSV joins the same server-minted batch, whose id is read
  // off it on submit. Lazily created once via useState so the instance is stable across renders.
  const [uploader] = useState(() => createSendBundleUploader());

  const handleSubmit = useCallback(
    async (values: SendValidatorSetupValues): Promise<void> => {
      const batchApiId = uploader.batchApiId();
      // The required rule blocks submit while no file is uploaded; guard anyway so an empty batch can
      // never reach the request body.
      if (_isNotEmpty(values.attachment_api_ids) === false || _isNil(batchApiId)) {
        return;
      }
      setSubmitError(null);
      const response = await AgentSessionService.createSendValidatorSession({ batch_api_id: batchApiId });
      if (response.type === ResponseType.Success && _notNil(response.body)) {
        const sessionApiId = response.body.api_id;
        navigate(webRoute('agents.session', { slug: agentUrlSlugMap.send_validator, sessionApiId }));
        return;
      }
      setSubmitError(response.error?.message ?? 'Failed to start the SEND validator. Please try again.');
    },
    [navigate, uploader]
  );

  const steps: Array<StepConfig> = useMemo(
    () => [
      {
        id: 'upload',
        title: 'Upload SEND CSV exports',
        description:
          'Upload one CSV per SEND domain (.csv). Each file is validated on its own and the bundle is checked for cross-domain consistency.',
        required: true,
        fields: [
          {
            type: 'upload',
            name: 'attachment_api_ids',
            label: 'SEND CSV exports',
            accept: CSV_ACCEPT,
            helpText: `One CSV per SEND domain · up to ${MAX_BUNDLE_FILES} files`,
            multiple: true,
            maxFiles: MAX_BUNDLE_FILES,
            uploadFile: uploader.uploadFile,
            // Stored values are opaque attachment_api_ids, not URLs; resolve display names off the
            // uploader so a re-seeded card shows the real filename instead of a placeholder.
            fileNameFor: uploader.fileNameFor,
            rules: { required: 'Upload at least one SEND CSV export' },
          },
        ],
      },
    ],
    [uploader]
  );

  return (
    <div className="mt3" data-test-component="SendValidatorSetupForm" data-testid="send-validator-setup-form">
      <VerticalStepForm<SendValidatorSetupValues>
        steps={steps}
        defaultValues={{ attachment_api_ids: [] }}
        onSubmit={handleSubmit}
        description="Validate a bundle of SEND CSV exports against the SENDIG v3.1.1 spec, per-domain and across domains."
        submitDescription="Detects each file's domain, then you confirm domains on the session page before validation runs."
      />

      {_notNil(submitError) && (
        <div className="red mt3 f6" data-testid="send-validator-setup__submit-error">
          {submitError}
        </div>
      )}
    </div>
  );
};

export default SendValidatorSetupForm;
